import { useState } from 'react';

import dayjs from 'dayjs';
import { useSelector } from 'hooks';

import { Event } from '../../types';

import { PRIMARY } from 'constants/colors';

import { Box, Card, Tooltip } from '@mui/material';

import AssignEmploymentDialog from '../../components/AssignEmploymentDialog/AssignEmploymentDialog';

import { ContextMenuContainer } from '../../components';
import useCopyMode from '../../hooks/useCopyMode';
import DutyTooltip from '../../components/DutyTooltip/DutyTooltip';
import { Icon } from '@sdb/planning-ui';

type EventProps = {
  title: string;
  subtitle: string | null | undefined;
  event: Event;
  showActions: boolean;
  multiplePlanUnitsSelected?: boolean;
};

const EventCard = ({
  title,
  subtitle,
  event,
  showActions,
  multiplePlanUnitsSelected
}: EventProps) => {
  const { hasWorkPlacePlanningEditAccess, hasKidsPlanAccess } = useSelector(
    ({ grants }) => grants
  );
  const [dialogActive, setDialogActive] = useState<boolean>(false);
  const client = event?.clients?.[0];
  const { copyId, isCopyMode, pasteAppointment } = useCopyMode();

  const isIncludedInKidsRatio = event.duty?.isIncludedInKidsRatio;
  const dutyCode = event.duty?.dutyCode?.dutyCode;

  return (
    <Tooltip
      enterNextDelay={500}
      title={
        <DutyTooltip
          event={event as unknown as Event}
          multiplePlanUnitsSelected={multiplePlanUnitsSelected}
        />
      }
    >
      <Card
        variant="outlined"
        sx={{
          boxSizing: 'border-box',
          height: '100%',
          padding: '8px 8px 8px 10px',
          borderRadius: '4px',
          border:
            isCopyMode && event.id == copyId ? `2px dashed ${PRIMARY}` : 'auto',
          borderLeft: !event.isOpen ? `4px solid ${PRIMARY}` : 'auto',
          bgcolor: !event.isOpen ? 'lightGray.main' : '#fdefd6',
          cursor:
            event.workPlace && showActions && (isCopyMode || event.isOpen)
              ? 'pointer'
              : 'auto'
        }}
        onClick={() => {
          if (showActions && event.workPlace)
            if (isCopyMode && event.workPlace?.entityId) {
              pasteAppointment({
                organizationalUnitId: event.organizationalUnit!.id,
                workPlaceEntityId: event.workPlace!.entityId,
                activityEntityId: event.activity
                  ? event.activity?.entityId
                  : undefined,
                startDate: event.start
              });
            } else {
              if (event.isOpen) {
                setDialogActive(true);
              }
            }
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={(e) => dialogActive && e.stopPropagation()}
        >
          <Box display="flex" flexDirection="column" alignItems="start">
            {title || (dutyCode && <Box>{title || dutyCode}</Box>)}
            {subtitle && (
              <Box
                fontSize="12px"
                sx={{
                  cursor: !event.isOpen ? 'pointer' : 'auto'
                }}
                onClick={() =>
                  !event.isOpen && event.workPlace && setDialogActive(true)
                }
              >
                {subtitle}
              </Box>
            )}
            <Box fontSize="11px" fontFamily="SourceSansPro-Regular">
              {`${dayjs(event.start).format('HH:mm')} - ${dayjs(
                event.end
              ).format('HH:mm')}`}
            </Box>
            {event.noteInternal && (
              <Tooltip title={event.noteInternal}>
                <Icon type="note" color="error" fontSize="small" />
              </Tooltip>
            )}
          </Box>
          {!isIncludedInKidsRatio && hasKidsPlanAccess && (
            <Box display="flex" alignItems="center">
              <Icon
                type="triangle-exclamation"
                fontSize="small"
                color="warning"
              />
            </Box>
          )}
          {showActions && (
            <Box onClick={(e) => e.stopPropagation()}>
              <ContextMenuContainer
                eventId={event.id}
                clientId={client?.id}
                schedulingLabel={event.schedulingLabel}
                scheduleStart={event.scheduleStart}
                hasAppointmentGrouping={!!event.appointmentGrouping}
                hasEmployee={!!event.employees?.[0]}
                isEditable={event.isEditable}
                isCreditable={event.isCreditable}
                isCredit={event.isCredit}
                isAbsent={false}
                eventIsOpen={event.isOpen}
                isAssigned={!!event.isAssigned}
                isProject={!!event.project}
                isWorkPlace={!!event.workPlace}
              />
            </Box>
          )}
          {showActions && event.workPlace && hasWorkPlacePlanningEditAccess && (
            <AssignEmploymentDialog
              open={dialogActive}
              event={event}
              date={event.start}
              onClose={() => setDialogActive(false)}
            />
          )}
        </Box>
      </Card>
    </Tooltip>
  );
};

export default EventCard;
